import { icons } from "shared/constants/assets";
import { createStore } from "zustand/vanilla";
import { isNil, isNull } from "lodash";
import { configRepo } from "./config";
import { gameRepo } from "./game";

export interface IItemNav {
  id: string;
  icon: keyof typeof icons;
  iconInactive?: keyof typeof icons | undefined;
  isHide?: boolean;
  isDisabled?: boolean;
  menus: string[];
  event?: () => void;
}

export interface INavRepo {
  navs: IItemNav[];
  setActive: (active: {
    nav: null | string;
    menu?: null | string;
    id?: null | string;
  }) => void;
  active: {
    nav: null | string;
    menu?: null | string;
    id?: null | string;
  };
  toggleUI: (options?: { forceState?: null | boolean }) => void;
  updateNav: (id: string, data: Partial<IItemNav>) => void;
  openNav: (newNav: string, id?: string) => void;
  toggleNav: (newNav: string) => void;
}

export const navRepo = createStore<INavRepo>((set, get) => {
  const checkNav = (nav: string) => {
    const { navs } = get();

    return navs.some(({ id }) => nav === id);
  };

  return {
    setActive: ({ nav, menu, id = null }) => {
      if (isNull(nav)) {
        set({
          active: {
            nav: null,
            menu: null,
            id: null,
          },
        });

        return;
      }

      if (!nav) {
        return;
      }

      const { active, navs } = get();

      const currentNav = navs.find(({ id }) => nav === id);

      if (!currentNav) {
        return;
      }

      set({
        active: {
          nav,
          id,
          menu:
            menu ||
            (active.menu && currentNav.menus.includes(active.menu)
              ? active.menu
              : currentNav.menus[0]),
        },
      });

      configRepo.setState({
        isUIActive: true,
      });
    },

    active: {
      nav: "settings",
      menu: "settings",
    },

    navs: [
      {
        id: "bonus_buy",
        icon: "menu_bonus_buy",
        iconInactive: "menu_bonus_buy_inactive",
        menus: ["bonus_buy"],
      },
      {
        id: "autoplay",
        isHide: true, // default autoplay_enabled value is false
        icon: "menu_autoplay",
        menus: ["autoplay"],
      },
      {
        id: "win_history",
        isHide: true, // default show_win_history value is false
        icon: "menu_win_history",
        menus: ["win_history"],
        event: () => gameRepo.getState().send("clicked_win_history"),
      },
      {
        id: "settings",
        icon: "menu_settings",
        menus: ["settings"],
      },
      {
        id: "lobby",
        isHide: true, // default show_lobby_button value is false
        icon: "menu_home",
        menus: ["lobby"],
        event: () => gameRepo.getState().send("clicked_lobby"),
      },
      {
        id: "home",
        isHide: true, // default show_home_button value is false
        icon: "menu_home",
        menus: ["home"],
        event: () => gameRepo.getState().send("clicked_home"),
      },
      {
        id: "tournaments",
        isHide: true, // default is hidden. show if notification_server_url and notification_server_token are not null
        icon: "menu_tournament",
        menus: ["tournaments"],
      },
    ],

    toggleUI: ({ forceState } = { forceState: null }) => {
      const { isUIActive } = configRepo.getState();

      configRepo.setState({
        isUIActive: !isNil(forceState) ? forceState : !isUIActive,
      });

      set({
        active: {
          nav: "settings",
          menu: "settings",
        },
      });
    },

    openNav: (nav, id) => {
      const resolvedNav = nav === "info" ? "settings" : nav;

      if (!checkNav(resolvedNav)) return;

      configRepo.setState({
        isUIActive: true,
      });

      get().setActive({
        nav: resolvedNav,
        id,
      });
    },

    toggleNav: (newNav) => {
      const {
        active: { nav },
      } = get();
      const { isUIActive } = configRepo.getState();

      const isNav = nav === newNav;

      configRepo.setState({
        isUIActive: !isNav,
      });

      if (!isUIActive) {
        return get().setActive({
          nav: newNav,
        });
      }

      get().setActive({
        nav: isNav ? null : newNav,
      });
    },

    updateNav: (id: string, data: Partial<IItemNav>) => {
      const { navs } = get();

      set({
        navs: navs.map((item) =>
          item.id === id
            ? {
                ...item,
                ...data,
              }
            : item
        ),
      });
    },
  };
});
